import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PrimaryButton, SecondaryButton } from "../../../../../components/common";
import { Edit } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useState } from "react";
import { PutQuestionnaireResponse } from "../../../../../services/fhir/patientsummary/questionnaireResponse/PutQuestionnaireResponse";
import { GetQuestionnaireResponseById } from "../../../../../services/fhir/patientsummary/questionnaireResponse/GetQuestionnaireResponse";
import { useQuestionnaireContext } from "../../../../../contexts/QuestionnaireContext";

interface EditResultExamProps {
    editDialogOpen: boolean;
    handleCloseEditDialog: () => void;
    handleSaveEdit: () => void;
    name: string;
    editedValue: string;
    setEditedValue: (value: string) => void;
    editedDate: any;
    setEditedDate: (date: any) => void;
    selectedResult: any;
    editedUnit: string;
    setEditedUnit: (unit: string) => void;
}

export default function EditResultExam({
    editDialogOpen,
    handleCloseEditDialog,
    handleSaveEdit,
    name,
    editedValue,
    setEditedValue,
    editedDate,
    setEditedDate,
    editedUnit,
    setEditedUnit,
    selectedResult
}: EditResultExamProps) {
    const [isSaving, setIsSaving] = useState(false);
    const { updateSingleQuestionnaireResponse } = useQuestionnaireContext();

    const saveEditedExamResult = async () => {
        try {
            setIsSaving(true);
            const questionnaireResponseId = selectedResult?.questionnaireResponseId;
            const patient = selectedResult?.patient;

            if (!selectedResult) {
                toast.error("Não foi possível identificar o resultado a ser editado");
                return;
            }

            if (!questionnaireResponseId) {
                toast.error("ID do questionário não encontrado");
                return;
            }

            if (!patient) {
                toast.error("Paciente não encontrado");
                return;
            }

            const questionnaireResponseData = await GetQuestionnaireResponseById(questionnaireResponseId, patient);

            if (!questionnaireResponseData || !questionnaireResponseData.entry || questionnaireResponseData.entry.length === 0) {
                toast.error("Não foi possível obter o questionário completo");
                return;
            }

            const completeResponse = questionnaireResponseData.entry[0].resource;

            if (!completeResponse) {
                toast.error("Não foi possível obter o questionário completo");
                return;
            }

            const examResultsItem = completeResponse.item?.find(
                (item: any) => item.text === "Resultado de exames"
            );

            if (!examResultsItem) {
                toast.error("Seção de resultados de exames não encontrada");
                return;
            }

            const examName = selectedResult?.item?.[0]?.answer?.[0]?.valueString;

            if (!examName) {
                toast.error("Nome do exame não encontrado");
                return;
            }

            let found = false;
            let updatedExamItem = null;

            for (let i = 0; i < examResultsItem.item.length; i++) {
                const examItem = examResultsItem.item[i];
                const nameItem = examItem?.item?.find(
                    (subItem: any) => subItem.text === "Nome do exame"
                );

                if (nameItem?.answer?.[0]?.valueString === examName) {
                    const valueItem = examItem?.item?.find(
                        (subItem: any) => subItem.text === "Valor do resultado"
                    );

                    if (valueItem?.answer) {
                        valueItem.answer[0].valueString = editedValue;
                    }

                    const dateItem = examItem?.item?.find(
                        (subItem: any) => subItem.text === "Data de realização"
                    );

                    if (dateItem?.answer) {
                        dateItem.answer[0].valueDate = editedDate.format("YYYY-MM-DD");
                    }

                    let unitItem = examItem?.item?.find(
                        (subItem: any) => subItem.text === "Unidade"
                    );

                    if (unitItem) {
                        if (unitItem.answer) {
                            unitItem.answer[0].valueString = editedUnit;
                        } else {
                            unitItem.answer = [{ valueString: editedUnit }];
                        }
                    } else {
                        examItem.item.push({
                            text: "Unidade",
                            linkId: `${examItem.linkId}.unidade`,
                            answer: [{ valueString: editedUnit }]
                        });
                    }

                    updatedExamItem = examItem;
                    found = true;
                    break;
                }
            }

            if (!found) {
                toast.error("Exame específico não encontrado no questionário");
                return;
            }

            const resourceId = completeResponse.id;
            if (!resourceId) {
                toast.error("ID do recurso não encontrado");
                return;
            }

            await PutQuestionnaireResponse(completeResponse, resourceId);

            updateSingleQuestionnaireResponse(resourceId, completeResponse);

            if (selectedResult && updatedExamItem) {
                const valueItem = selectedResult?.item?.find(
                    (subItem: any) => subItem.text === "Valor do resultado"
                );
                if (valueItem?.answer) {
                    valueItem.answer[0].valueString = editedValue;
                }

                const dateItem = selectedResult?.item?.find(
                    (subItem: any) => subItem.text === "Data de realização"
                );
                if (dateItem?.answer) {
                    dateItem.answer[0].valueDate = editedDate.format("YYYY-MM-DD");
                }

                let unitItem = selectedResult?.item?.find(
                    (subItem: any) => subItem.text === "Unidade"
                );

                if (unitItem) {
                    if (unitItem.answer) {
                        unitItem.answer[0].valueString = editedUnit;
                    } else {
                        unitItem.answer = [{ valueString: editedUnit }];
                    }
                } else {
                    selectedResult.item.push({
                        text: "Unidade",
                        linkId: `${selectedResult.linkId}.unidade`,
                        answer: [{ valueString: editedUnit }]
                    });
                }
            }

            toast.success("Resultado atualizado com sucesso!");
            handleSaveEdit();
        } catch (error) {
            console.error("Erro ao salvar alterações:", error);
            toast.error("Erro ao salvar alterações. Tente novamente.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog
            open={editDialogOpen}
            onClose={handleCloseEditDialog}
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 0px 20px 2px rgba(0, 0, 0, 0.2)",
                    padding: "16px",
                }
            }}
            fullWidth
        >
            <DialogTitle sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ color: "neutral600.main", display: "flex", alignItems: "center", gap: "8px", fontSize: "18px", fontWeight: "medium" }}>
                    <Edit sx={{ color: "neutral600.main" }} fontSize="small" />
                    Editar resultado
                </Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold", color: "neutral700.main" }}>{name}</Typography>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Stack>
                        <TextField
                            label="Valor do exame"
                            value={editedValue}
                            onChange={(e: any) => setEditedValue(e.target.value)}
                            fullWidth
                            variant="outlined"
                            margin="normal"
                        />
                        <TextField
                            label="Unidade"
                            value={editedUnit}
                            onChange={(e: any) => setEditedUnit(e.target.value)}
                            fullWidth
                            variant="outlined"
                            margin="normal"
                        />
                    </Stack>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Data de realização"
                            value={editedDate}
                            onChange={(date) => setEditedDate(date)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    margin: 'normal'
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingX: 3 }}>
                <SecondaryButton
                    onClick={handleCloseEditDialog}
                    width="150px"
                    height="40px"
                >
                    Cancelar
                </SecondaryButton>
                <PrimaryButton
                    onClick={saveEditedExamResult}
                    width="150px"
                    height="40px"
                    disabled={isSaving}
                >
                    {isSaving ? "Salvando..." : "Salvar"}
                </PrimaryButton>
            </DialogActions>
        </Dialog>
    )
}